import React from 'react';

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

const fallbackStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  padding: 24,
  textAlign: 'center',
  background: 'rgba(0, 0, 0, 1)',
  color: '#9aa0a6',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const messageStyle: React.CSSProperties = { fontSize: 15, lineHeight: 1.5, maxWidth: 360 };
const buttonRowStyle: React.CSSProperties = { display: 'flex', gap: 12 };
const baseButtonStyle: React.CSSProperties = {
  appearance: 'none',
  fontSize: 14,
  padding: '10px 20px',
  borderRadius: 8,
  cursor: 'pointer',
};
const primaryButtonStyle: React.CSSProperties = {
  ...baseButtonStyle,
  border: '1px solid #3c4043',
  background: '#e8eaed',
  color: '#1a1a1a',
};
const secondaryButtonStyle: React.CSSProperties = {
  ...baseButtonStyle,
  border: '1px solid #3c4043',
  background: '#1a1a1a',
  color: '#e8eaed',
};

/**
 * App-level backstop for render-time crashes. React Router already routes most
 * page render errors to its per-route `errorElement`; this catches anything that
 * slips past that boundary — errors thrown above the router, inside the Redux /
 * i18n providers, or in the root chrome — so a single broken component (e.g. a
 * section that throws when an API returns an unexpected shape) shows a
 * recoverable message instead of unmounting the whole tree to a black screen.
 *
 * It does NOT cover a module that fails to *load* (so React never mounts at all);
 * that hangs the static `index.html` splash and is handled by the boot watchdog
 * in `index.html` (`window.__appBooted`).
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // No reporting backend wired yet — surface it in the console so the crash
    // isn't swallowed silently behind the fallback.
    console.error('App ErrorBoundary caught a render error', error, info);
  }

  handleRetry = () => {
    // Re-attempt the render. Recovers from a transient error (a slow/aborted
    // request that has since settled); a deterministic crash just re-trips the
    // boundary, leaving Reload as the way out.
    this.setState({ hasError: false });
  };

  handleReload = () => {
    // Clear the boot watchdog's one-shot reload guard so a fresh load gets its
    // own retry budget.
    try {
      sessionStorage.removeItem('app-boot-reloaded');
    } catch (e) {
      /* sessionStorage can throw in private mode — ignore */
    }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div style={fallbackStyle}>
        <div style={messageStyle}>Something went wrong.</div>
        <div style={buttonRowStyle}>
          <button type="button" style={primaryButtonStyle} onClick={this.handleRetry}>
            Try again
          </button>
          <button type="button" style={secondaryButtonStyle} onClick={this.handleReload}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
